import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom'
import { isNil } from 'lodash'
import { useIdentity } from 'pages/Identity/Login'
import { Grid, Typography, Select, MenuItem, FormControl, InputLabel, Box, Alert, Snackbar, Menu, MenuItem as MenuItemComponent, useTheme, useMediaQuery, Stack, Pagination, CircularProgress, Modal, Chip, Button } from '@mui/material'
import UploadIcon from '@mui/icons-material/Upload'
import { List } from 'components/Listing/List'
import Loader from 'components/Loader'
import { fetchCandidateJobs, fetchHospitalJobs, fetchPublicJobs, JOBS_PER_PAGE } from 'src/redux/jobs'
import { fetchJobFilterValues } from 'src/redux/filters'
import JobContent from './JobContent'
import { useJobs } from './useJobs'
import JobDrawer from 'components/Drawers/JobDrawer/JobDrawer'
import SmartUploadDrawer from 'components/Drawers/SmartUploadDrawer/SmartUploadDrawer'
import { Filters } from '../../components/Filters'
import { FilterType } from '../../components/Filters/filterDefinitions'
import { fetchUpdateCandidateJob } from 'src/redux/candidateJob'
import { applyFilters } from 'components/Filters/helpers'
import { fetchCreateUserTutorialView } from 'src/redux/users'
import { USER_TUTORIAL_VIEW_ENUM } from 'data/enums'

// AI Components
import MyStethiAIIcon from '/src/components/Icons/MyStethiAIIcon'
import AIJobCard from './AI/AIJobCard'
import JobDetailPanel from './AI/JobDetailPanel'
import AIChatBot from './AI/AIChatBot'
import ChatBottomBar from './AI/ChatBottomBar'
import { wrapTraditionalJobsAsRanked, mapJobListingArgumentsToFilterParams, buildFilterChangeMessage } from './AI/filterSyncHelpers'
import { generateExampleQueries } from './AI/helpers'
import {
  updateAiJobFavorite,
  addChatMessage,
  sendChatMessage,
  addSystemMessage,
  setActiveSearchSource,
} from '../../redux/aiJobSearch'

const AI_SEARCH_UPDATE_TUTORIAL = 'CandidateAISearchUpdateApr2026'
const AI_SEARCH_UPDATE_CUTOFF = '2026-04-05'

const Jobs = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()
  const mobile = useMediaQuery(theme.breakpoints.down('md'))
  const signedIn = useSelector(state => state.identity.signedIn)

  const candidate = useSelector(state => state.candidate)
  const {
    candidateType,
    specialties = [],
    loading: candidateLoading,
  } = candidate
  const { loading: employerUserLoading } = useSelector(state => state.employerUser)
  const loading = candidateLoading || employerUserLoading
  const { employerId, employerUserId, candidateId, userId, isEmployerAdmin } = useIdentity()
  const [activeItem, setActiveItem] = useState()
  const [page, setPage] = useState(1)
  const pageSize = JOBS_PER_PAGE
  const { jobsList = [], isLoadingJobs } = useSelector(state => state.jobs)
  const { loading: isLoadingFilters } = useSelector(state => state.filters)
  const filterValues = useSelector(state => state.filters.jobFilterValues) || {}
  const totalJobs = useSelector(state => state.jobs.totalJobs)
  const totalPages = Math.ceil(totalJobs / pageSize)
  const dataCall = employerUserId ? fetchHospitalJobs : signedIn ? fetchCandidateJobs : fetchPublicJobs
  const [searchParams, setSearchParams] = useSearchParams()
  const candidateTypeId = searchParams.get('candidateTypeId')
  const specialtyIdsParam = searchParams.get('specialtyIds')
  const [displayedData, setDisplayedData] = useState(jobsList)
  const [isApplyingFilters, setIsApplyingFilters] = useState(false)

  const [favoritesOnly, setFavoritesOnly] = useState(false)
  const [archivedFilter, setArchivedFilter] = useState('all')

  // AI-related state from Redux
  const activeSearchSource = useSelector(state => state.aiJobSearch.activeSearchSource)
  const isAIMode = activeSearchSource === 'ai'
  const { rankedJobs, jobListingArguments, resolvedFilters, conversationId, isAiTyping, loading: aiLoading } = useSelector(state => state.aiJobSearch)

  // Derived flags
  const isCandidate = signedIn && !employerUserId
  // Floating chat widget state
  const [chatOpen, setChatOpen] = useState(true)
  const [selectedJob, setSelectedJob] = useState(null)
  const [candidatePage, setCandidatePage] = useState(1)

  // AI results pulse animation state
  const [jobListPulseActive, setJobListPulseActive] = useState(false)
  const previousRankedJobsRef = useRef(rankedJobs)

  // Smart Job Upload notification state
  const [showUploadSuccess, setShowUploadSuccess] = useState(false)
  const [uploadedJobsCount, setUploadedJobsCount] = useState(0)

  // Upload menu state
  const [uploadMenuAnchor, setUploadMenuAnchor] = useState(null)

  // Job drawer state (for manual upload)
  const { drawerJobOpen, setDrawerJobOpen } = useJobs()

  // Smart upload drawer state
  const [smartUploadDrawerOpen, setSmartUploadDrawerOpen] = useState(false)

  // AI search update modal state
  const [showAISearchUpdateModal, setShowAISearchUpdateModal] = useState(false)

  // Resizable panel state (for employer list/detail split)
  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    const saved = localStorage.getItem('jobsLeftPanelWidth')
    return saved ? parseInt(saved) : 420
  })
  const [isResizing, setIsResizing] = useState(false)

  // Resizable panel state (for candidate card list/detail split)
  const [candidateListWidth, setCandidateListWidth] = useState(() => {
    const saved = localStorage.getItem('jobsCandidateListWidth')
    return saved ? parseInt(saved) : 400
  })
  const [isCandidateResizing, setIsCandidateResizing] = useState(false)

  // Fallback labels for specialty IDs (used when filterValues hasn't loaded yet)
  const candidateSpecialtyLabels = useMemo(() => {
    const labels = {}
    specialties.forEach(s => { labels[String(s.id)] = s.name })
    return labels
  }, [specialties])

  // Compute display jobs: AI mode uses rankedJobs, traditional wraps jobsList
  const displayJobs = useMemo(() => {
    if (isCandidate && isAIMode) {
      return rankedJobs?.length > 0 ? rankedJobs : []
    }
    if (isCandidate) {
      return wrapTraditionalJobsAsRanked(displayedData)
    }
    return null // employer/not-signed-in use traditional List
  }, [isCandidate, isAIMode, rankedJobs, displayedData])

  // AI mode: client-side pagination over ranked results from chatbot
  const candidateTotalPages = isAIMode && displayJobs ? Math.ceil(displayJobs.length / JOBS_PER_PAGE) : 0
  const currentDisplayJobs = useMemo(
    () => {
      if (!displayJobs) return []
      if (isAIMode) return displayJobs.slice((candidatePage - 1) * JOBS_PER_PAGE, candidatePage * JOBS_PER_PAGE)
      return displayJobs // traditional mode: already server-paginated
    },
    [displayJobs, candidatePage, isAIMode],
  )

  // Pulse the job list panel when new AI results arrive
  useEffect(() => {
    if (
      rankedJobs !== previousRankedJobsRef.current &&
      rankedJobs.length > 0 &&
      isAIMode
    ) {
      setJobListPulseActive(true)
      const timer = setTimeout(() => setJobListPulseActive(false), 1500)
      previousRankedJobsRef.current = rankedJobs
      return () => clearTimeout(timer)
    }
    previousRankedJobsRef.current = rankedJobs
  }, [rankedJobs, isAIMode])

  // Listen for "view results" click from the chatbot to re-trigger pulse
  useEffect(() => {
    const handleViewResults = () => {
      setJobListPulseActive(false)
      requestAnimationFrame(() => setJobListPulseActive(true))
      const timer = setTimeout(() => setJobListPulseActive(false), 1500)
      return () => clearTimeout(timer)
    }
    window.addEventListener('ai-view-results', handleViewResults)
    return () => window.removeEventListener('ai-view-results', handleViewResults)
  }, [])

  // Reset candidate page when results change
  useEffect(() => {
    setCandidatePage(1)
  }, [rankedJobs, displayedData])

  // Auto-select first job when results change and no valid selection exists
  useEffect(() => {
    if (!currentDisplayJobs.length) {
      if (!isAiTyping) {
        setSelectedJob(null)
      }
      return
    }
    const selectedStillInList = selectedJob && currentDisplayJobs.some(
      rj => (rj.job?.id || rj.jobId) === (selectedJob.job?.id || selectedJob.jobId)
    )
    if (!selectedStillInList) {
      setSelectedJob(currentDisplayJobs[0])
    }
  }, [currentDisplayJobs, isAiTyping, selectedJob])

  // When switching back to traditional mode (e.g. after reset), restore default filters
  const previousSearchSourceRef = useRef(activeSearchSource)
  useEffect(() => {
    if (previousSearchSourceRef.current === 'ai' && activeSearchSource === 'traditional') {
      aiSyncGenerationRef.current += 1
      if (filterTriggeredTransitionRef.current) {
        filterTriggeredTransitionRef.current = false
      } else {
        setSearchParams(new URLSearchParams(), { replace: true })
      }
    }
    previousSearchSourceRef.current = activeSearchSource
  }, [activeSearchSource, candidateType, specialties, setSearchParams])

  // Bidirectional filter sync refs
  const aiSyncGenerationRef = useRef(0)
  const filterSyncGenerationRef = useRef(0)
  const previousParamsRef = useRef(new URLSearchParams(searchParams))
  const filterTriggeredTransitionRef = useRef(false)

  // Track previous filter values to detect filter-vs-page changes in the fetch effect
  const prevFilterSearchParamsRef = useRef(searchParams.toString())
  const prevFilterFavoritesRef = useRef(favoritesOnly)
  const prevFilterArchivedRef = useRef(archivedFilter)
  const skipPageResetFetchRef = useRef(false)

  // Guard for one-time profession default on initial load
  const hasDefaultedProfessionRef = useRef(false)

  // AI -> Filters sync: when AI returns jobListingArguments, update URL params
  // With resolved IDs from the backend, filterValues is no longer needed for mapping.
  useEffect(() => {
    if (activeSearchSource !== 'ai' || !jobListingArguments) return

    const aiParams = mapJobListingArgumentsToFilterParams(jobListingArguments, resolvedFilters, filterValues)
    const currentCandidateTypeId = searchParams.get('candidateTypeId')

    const newParams = new URLSearchParams(aiParams)
    if (currentCandidateTypeId && !newParams.has('candidateTypeId')) {
      newParams.set('candidateTypeId', currentCandidateTypeId)
    }

    aiSyncGenerationRef.current += 1
    previousParamsRef.current = new URLSearchParams(newParams)
    setSearchParams(newParams, { replace: true })
  }, [jobListingArguments, activeSearchSource])

  // Filters -> AI sync: when user manually changes filters, note the change and switch to traditional results
  useEffect(() => {
    if (aiSyncGenerationRef.current !== filterSyncGenerationRef.current) {
      filterSyncGenerationRef.current = aiSyncGenerationRef.current
      return
    }

    const message = buildFilterChangeMessage(previousParamsRef.current, searchParams, filterValues, candidateSpecialtyLabels)
    if (!message) return

    // Always show filter changes in the chat so the user sees them even before starting a conversation
    if (isCandidate) {
      dispatch(addSystemMessage(message))
    }
    previousParamsRef.current = new URLSearchParams(searchParams)

    // Switch to traditional results — the user is engaging with filter UI, not the chatbot.
    // The chatbot conversation stays intact; system messages preserve context for later.
    if (conversationId && activeSearchSource === 'ai') {
      filterTriggeredTransitionRef.current = true
      dispatch(setActiveSearchSource('traditional'))
    }
  }, [searchParams, conversationId, activeSearchSource, filterValues, isCandidate, dispatch])

  const exampleQueries = useMemo(
    () => generateExampleQueries(candidate),
    [candidate]
  )

  const startResize = (e) => {
    if (mobile) return
    setIsResizing(true)
    e.preventDefault()
  }

  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e) => {
      requestAnimationFrame(() => {
        const container = document.getElementById('jobs-container')
        if (!container) return

        const containerRect = container.getBoundingClientRect()
        const newWidth = e.clientX - containerRect.left

        if (newWidth >= 250 && newWidth <= 1000) {
          setLeftPanelWidth(newWidth)
        }
      })
    }

    const stopResize = () => {
      setIsResizing(false)
      localStorage.setItem('jobsLeftPanelWidth', leftPanelWidth.toString())
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', stopResize)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', stopResize)
    }
  }, [isResizing, leftPanelWidth, mobile])

  const startCandidateResize = (e) => {
    if (mobile) return
    setIsCandidateResizing(true)
    e.preventDefault()
  }

  useEffect(() => {
    if (!isCandidateResizing) return

    const handleMouseMove = (e) => {
      requestAnimationFrame(() => {
        const container = document.getElementById('candidate-jobs-container')
        if (!container) return

        const containerRect = container.getBoundingClientRect()
        const newWidth = e.clientX - containerRect.left

        if (newWidth >= 280 && newWidth <= 900) {
          setCandidateListWidth(newWidth)
        }
      })
    }

    const stopResize = () => {
      setIsCandidateResizing(false)
      localStorage.setItem('jobsCandidateListWidth', candidateListWidth.toString())
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', stopResize)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', stopResize)
    }
  }, [isCandidateResizing, candidateListWidth, mobile])

  const handleListItemClick = currId => {
    const activeJob = !isNil(currId)
      ? displayedData.find(j => `${j.id}` === `${currId}`)
      : displayedData.length > 0
        ? displayedData[0]
        : null
    setActiveItem(activeJob)
  }

  useEffect(() => {
    if (searchParams.size === 0 && !employerUserId && !isAIMode) {
      if (isCandidate && candidateLoading) return
      dispatch(dataCall({ page, pageSize }))
    }
  }, [signedIn, dispatch, dataCall, page, pageSize, employerUserId, isAIMode, isCandidate, candidateLoading])

  useEffect(() => {
    if (!mobile && jobsList.length > 0) {
      setActiveItem(jobsList[0])
    } else {
      setActiveItem(null)
    }
  }, [jobsList, mobile])

  useEffect(() => {
    if (location.state?.newlyUploaded) {
      setUploadedJobsCount(location.state.newlyUploaded)
      setShowUploadSuccess(true)
      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  useEffect(() => {
    const isExistingUser = candidate.createdAt && new Date(candidate.createdAt) < new Date(AI_SEARCH_UPDATE_CUTOFF)
    if (
      isCandidate &&
      isExistingUser &&
      !candidate.loading &&
      !candidate.tutorialViews?.includes(AI_SEARCH_UPDATE_TUTORIAL)
    ) {
      setShowAISearchUpdateModal(true)
    }
  }, [candidate.loading, candidate.tutorialViews, candidate.createdAt, isCandidate])

  useEffect(() => {
    if (candidateTypeId) {
      dispatch(
        fetchJobFilterValues({
          employerId: employerId,
          candidateTypeId: candidateTypeId,
          specialtyIds: specialtyIdsParam ? specialtyIdsParam.split('|') : [],
        }),
      )
    }
    else {
      dispatch(
        fetchJobFilterValues({
          employerId: employerId,
        }),
      )
    }
  }, [candidateTypeId, employerId, specialtyIdsParam])

  useEffect(() => {
    if (
      hasDefaultedProfessionRef.current ||
      !isCandidate ||
      candidateLoading ||
      !candidateType?.id
    ) return

    hasDefaultedProfessionRef.current = true

    if (!searchParams.has('candidateTypeId')) {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('candidateTypeId', String(candidateType.id))
      aiSyncGenerationRef.current += 1
      previousParamsRef.current = new URLSearchParams(newParams)
      setSearchParams(newParams, { replace: true })
    }
  }, [isCandidate, candidateLoading, candidateType, searchParams, setSearchParams])

  useEffect(() => {
    setDisplayedData(jobsList)
  }, [jobsList])

  useEffect(() => {
    if (skipPageResetFetchRef.current) {
      skipPageResetFetchRef.current = false
      return
    }

    if ((searchParams.size > 0 || employerUserId || favoritesOnly || archivedFilter !== 'all') && !isAIMode) {
      const filtersChanged =
        prevFilterSearchParamsRef.current !== searchParams.toString() ||
        prevFilterFavoritesRef.current !== favoritesOnly ||
        prevFilterArchivedRef.current !== archivedFilter

      prevFilterSearchParamsRef.current = searchParams.toString()
      prevFilterFavoritesRef.current = favoritesOnly
      prevFilterArchivedRef.current = archivedFilter

      let effectivePage = page
      if (filtersChanged && page !== 1) {
        effectivePage = 1
        skipPageResetFetchRef.current = true
        setPage(1)
      }

      const effectiveParams = new URLSearchParams(searchParams)
      if (favoritesOnly) effectiveParams.set('favorite', 'true')
      if (archivedFilter !== 'all') effectiveParams.set('archived', archivedFilter)

      setIsApplyingFilters(true)
      applyFilters({
        candidateId,
        employerUserId,
        searchParams: effectiveParams,
        dispatch,
        getData: dataCall,
        page: effectivePage,
        pageSize,
        employerId,
        isEmployerAdmin,
        userId,
      })
      const timer = setTimeout(() => {
        setIsApplyingFilters(false)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [searchParams, isAIMode, page, favoritesOnly, archivedFilter])

  const toggleFavoritesFilter = () => {
    setFavoritesOnly(!favoritesOnly)
  }

  const handleArchivedFilterChange = (event) => {
    setArchivedFilter(event.target.value)
  }

  const handleAISearchUpdateDismiss = () => {
    setShowAISearchUpdateModal(false)
    dispatch(fetchCreateUserTutorialView({
      userId,
      tutorial: USER_TUTORIAL_VIEW_ENUM[AI_SEARCH_UPDATE_TUTORIAL],
      tutorialName: AI_SEARCH_UPDATE_TUTORIAL,
      candidateId: candidate.id,
    }))
  }

  const handleUploadMenuClick = (event) => {
    setUploadMenuAnchor(event.currentTarget)
  }

  const handleUploadMenuClose = () => {
    setUploadMenuAnchor(null)
  }

  const handleSmartUpload = () => {
    handleUploadMenuClose()
    setSmartUploadDrawerOpen(true)
  }

  const handleManualUpload = () => {
    handleUploadMenuClose()
    setDrawerJobOpen(true)
  }

  const resultCount = isAIMode ? (displayJobs?.length || 0) : (totalJobs || 0)
  const leftPanelHeader = `Results: ${resultCount.toLocaleString()} Jobs`

  const applyJobFilters = ({ searchParams }) => {
    if (!isAIMode) {
      setPage(1)

      const effectiveParams = new URLSearchParams(searchParams)
      if (favoritesOnly) effectiveParams.set('favorite', 'true')
      if (archivedFilter !== 'all') effectiveParams.set('archived', archivedFilter)

      applyFilters({
        candidateId,
        employerUserId,
        searchParams: effectiveParams,
        dispatch,
        getData: dataCall,
        page: 1,
        pageSize,
        employerId,
        isEmployerAdmin,
        userId,
      })
    }
  }

  const onFavoriteClick = ({ favorite, jobId }) => {
    if (isAIMode) {
      dispatch(updateAiJobFavorite({ jobId, favorite }))
      if (selectedJob && (selectedJob.job?.id || selectedJob.jobId) === jobId) {
        setSelectedJob({
          ...selectedJob,
          job: {
            ...selectedJob.job,
            candidateJob: {
              ...selectedJob.job?.candidateJob,
              favorite,
            },
          },
        })
      }
    } else {
      const updatedData = displayedData.map(item =>
        item.id === jobId
          ? {
              ...item,
              candidateJob: {
                ...item.candidateJob,
                favorite,
              },
            }
          : item,
      )

      setDisplayedData(updatedData)

      if (activeItem && activeItem.id === jobId) {
        const updatedActiveItem = {
          ...activeItem,
          candidateJob: {
            ...activeItem.candidateJob,
            favorite,
          },
        }
        setActiveItem(updatedActiveItem)
      }
    }

    dispatch(fetchUpdateCandidateJob({ candidateId, jobId, favorite }))
  }

  // AI job card click -> mobile: navigate to detail page, desktop: open detail panel
  const handleJobClick = useCallback(rankedJob => {
    const jobId = rankedJob.job?.id || rankedJob.jobId
    if (mobile) {
      navigate(`/jobs/ai/${jobId}`, { state: { rankedJob, conversationId } })
    } else {
      setSelectedJob(rankedJob)
    }
  }, [mobile, navigate, conversationId])

  // Browser back button closes detail panel on mobile
  useEffect(() => {
    const handlePopState = () => {
      setSelectedJob(null)
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  const handleExampleChipClick = useCallback((query) => {
    dispatch(addChatMessage({ type: 'user', message: query }))
    dispatch(sendChatMessage({ message: query, conversationId: undefined }))
  }, [dispatch])

  const handleChipDelete = useCallback((paramKey, value) => {
    const newParams = new URLSearchParams(searchParams)
    if (value === null) {
      newParams.delete(paramKey)
    } else {
      const current = newParams.get(paramKey)
      if (current) {
        const values = current.split('|').filter(v => v !== value)
        if (values.length > 0) {
          newParams.set(paramKey, values.join('|'))
        } else {
          newParams.delete(paramKey)
        }
      }
    }

    // Prevent the Filters->AI sync effect from double-processing this change
    aiSyncGenerationRef.current += 1
    previousParamsRef.current = new URLSearchParams(newParams)
    setSearchParams(newParams, { replace: true })

    // Note the change in chat history, then switch to traditional results.
    if (conversationId) {
      const message = buildFilterChangeMessage(new URLSearchParams(searchParams), newParams, filterValues, candidateSpecialtyLabels)
      if (message) {
        dispatch(addSystemMessage(message))
      }
      if (activeSearchSource === 'ai') {
        filterTriggeredTransitionRef.current = true
        dispatch(setActiveSearchSource('traditional'))
      }
    }
  }, [searchParams, setSearchParams, conversationId, activeSearchSource, filterValues, dispatch])


  // Candidate card-based job list
  const renderCandidateJobList = () => {
    const showLoading = (isLoadingJobs || loading || isApplyingFilters) && !isAIMode
    const showAiLoading = isAIMode && (aiLoading || isAiTyping) && currentDisplayJobs.length === 0

    if (showLoading || showAiLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, py: 4 }}>
          <CircularProgress sx={{ color: isAIMode ? theme.palette.ai.main : theme.palette.primary.main }} />
        </Box>
      )
    }

    if (currentDisplayJobs.length > 0) {
      return (
        <Stack
          spacing={1.5}
          sx={{
            px: 2,
            py: 1,
            pb: { xs: '72px', md: 0 },
            flex: 1,
            minWidth: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.grey[300],
              borderRadius: '2px',
            },
          }}
        >
          {currentDisplayJobs.map(rankedJob => {
            const jobId = rankedJob.job?.id || rankedJob.jobId
            return (
              <AIJobCard
                key={jobId}
                rankedJob={rankedJob}
                isActive={selectedJob?.job?.id === rankedJob.job?.id}
                onClick={() => handleJobClick(rankedJob)}
                conversationId={conversationId}
                onFavoriteClick={onFavoriteClick}
                isFavorite={!!rankedJob.job?.candidateJob?.favorite}
                isMobile={mobile}
              />
            )
          })}
          {isAIMode && candidateTotalPages > 1 && (
            <Box sx={{ py: 0.75, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={candidateTotalPages}
                page={candidatePage}
                onChange={(e, v) => setCandidatePage(v)}
                color='primary'
                size='small'
              />
            </Box>
          )}
          {!isAIMode && totalPages > 1 && (
            <Box sx={{ py: 0.75, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, v) => setPage(v)}
                color='primary'
                size='small'
              />
            </Box>
          )}
        </Stack>
      )
    }

    // Empty state
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, textAlign: 'center' }}>
        {(isLoadingJobs || loading) ? (
          <Loader />
        ) : (
          <Box sx={{ maxWidth: 360 }}>
            <Typography variant='h6' sx={{ color: theme.palette.grey[800], fontWeight: 600, mb: 0.5 }}>
              {isAIMode ? 'Ready to find your perfect job' : 'No jobs found'}
            </Typography>
            <Typography variant='body2' sx={{ color: theme.palette.grey[600], mb: 3 }}>
              {isAIMode
                ? 'Describe what you\'re looking for in the chat:'
                : 'Try adjusting your filters or search with Stethi AI.'
              }
            </Typography>
            {isAIMode && (
              <Stack spacing={1} sx={{ width: '100%' }}>
                {exampleQueries.map((query) => (
                  <Typography
                    key={query}
                    variant='body2'
                    onClick={() => handleExampleChipClick(query)}
                    sx={{
                      color: theme.palette.ai.dark,
                      cursor: 'pointer',
                      padding: '8px 12px',
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.grey[200]}`,
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        borderColor: theme.palette.ai.main,
                        color: theme.palette.ai.main,
                        backgroundColor: `${theme.palette.ai.light}20`,
                      },
                    }}
                  >
                    "{query}"
                  </Typography>
                ))}
              </Stack>
            )}
          </Box>
        )}
      </Box>
    )
  }

  // Employer traditional list+detail layout
  const renderEmployerLayout = () => (
    <Box id="jobs-container" sx={{
      display: 'flex',
      flex: 1,
      minHeight: 0,
      width: '100%',
      maxWidth: '100vw',
      overflow: 'hidden',
      px: { xs: 0, md: 4 },
    }}>
      <Grid
        container
        item
        xs={12}
        direction='column'
        alignContent='flex-start'
        sx={{
          height: '100%',
          minHeight: 0,
          flex: { xs: '1', md: `0 0 ${leftPanelWidth}px` },
          width: { xs: '100%', md: 'auto' },
          overflow: 'hidden',
          px: { xs: 0 },
          '& .MuiBox-root': {
            px: { xs: 0, md: 3 }
          }
        }}
      >
        <Grid
          container
          item
          alignItems='center'
          justifyContent='space-between'
          sx={{
            width: '100%',
            overflow: 'hidden',
            px: { xs: 2 },
            py: 2,
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            backgroundColor: 'white',
            '& .MuiBox-root': {
              px: { xs: 0, md: 3 }
            }
          }}
        >
          <Grid container item xs={12} alignItems="center" justifyContent="space-between">
            <Grid item xs="auto">
              <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                {leftPanelHeader}
              </Typography>
            </Grid>
            <Grid container item xs="auto" spacing={2} justifyContent="flex-end" alignItems="center">
              {signedIn && employerUserId && (
                <Grid item>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel sx={{ fontSize: '0.875rem' }}>Status</InputLabel>
                    <Select
                      value={archivedFilter}
                      onChange={handleArchivedFilterChange}
                      label="Status"
                      sx={{ fontSize: '0.875rem', height: '32px' }}
                    >
                      <MenuItem value="all" sx={{ fontSize: '0.875rem' }}>All Jobs</MenuItem>
                      <MenuItem value="true" sx={{ fontSize: '0.875rem' }}>Archived</MenuItem>
                      <MenuItem value="false" sx={{ fontSize: '0.875rem' }}>Active</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>

        <Grid container alignItems='flex-start' sx={{
          maxHeight: '100%',
          overflow: 'auto',
          flex: 1,
          width: '100%'
        }}>
          <List
            setPage={setPage}
            page={page}
            data={displayedData}
            listType='job'
            handleListItemClick={handleListItemClick}
            dataTotal={totalJobs}
            totalPages={totalPages}
            signedIn={signedIn}
            setDisplayedData={setDisplayedData}
            onFavoriteClick={onFavoriteClick}
            setActiveItem={setActiveItem}
          />
        </Grid>
      </Grid>

      {/* Resizable Divider */}
      <Box
        onMouseDown={startResize}
        sx={{
          display: { xs: 'none', md: 'block' },
          flexShrink: 0,
          width: '4px',
          cursor: 'col-resize',
          backgroundColor: 'transparent',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '1px',
            height: '100%',
            backgroundColor: theme.palette.divider,
          },
          '&:hover, &:active': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      />

      {/* Right Panel */}
      <Grid
        container
        item
        xs={12}
        direction='column'
        sx={{
          height: '100%',
          minHeight: 0,
          flex: { xs: '1', md: '1' },
          pl: { md: 3 },
          overflow: 'auto',
          display: { xs: 'none', md: 'flex' },
        }}
      >
        <JobContent
          item={activeItem}
          signedIn={signedIn}
          employerUserId={employerUserId}
          onFavoriteClick={onFavoriteClick}
          jobs={jobsList}
          activeItem={activeItem}
          onUploadMenuClick={handleUploadMenuClick}
          uploadMenuAnchor={uploadMenuAnchor}
          onUploadMenuClose={handleUploadMenuClose}
          onSmartUpload={handleSmartUpload}
          onManualUpload={handleManualUpload}
        />
      </Grid>
    </Box>
  )

  return (
    <>
      <Box
        className="ai-smart-search-container"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
          transition: 'margin-right 0.3s ease',
        }}
      >
        {/* Main content area */}
        {isCandidate ? (
          // Candidate: card-based layout with AI sidebar
          <Box
            id="candidate-jobs-container"
            data-ai-sidebar-parent
            sx={{
              display: 'flex',
              width: '100%',
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              overflow: 'hidden',
              ...(isAiTyping && {
                pointerEvents: 'none',
                opacity: 0.6,
              }),
            }}
          >
            {/* Job list panel */}
            <Box sx={{
              flex: { xs: '1', md: `0 0 ${candidateListWidth}px` },
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
              ...(jobListPulseActive && {
                '@keyframes aiResultsPulse': {
                  '0%': { boxShadow: `inset 0 0 0 0 transparent` },
                  '40%': { boxShadow: `inset 0 0 20px 4px ${theme.palette.ai.light}4D` },
                  '100%': { boxShadow: `inset 0 0 0 0 transparent` },
                },
                animation: 'aiResultsPulse 1.5s ease-out',
              }),
            }}>
              <Box sx={{ flexShrink: 0 }}>
                <Filters
                  filterType={FilterType.JOB}
                  filterValues={filterValues}
                  listType='job'
                  loading={isLoadingFilters}
                  applyFilters={applyJobFilters}
                  disabled={isAiTyping || isLoadingJobs || isApplyingFilters}
                  resultCount={isAIMode ? displayJobs?.length : totalJobs}
                  favoritesOnly={favoritesOnly}
                  onToggleFavorites={signedIn && !employerUserId ? toggleFavoritesFilter : undefined}
                  isAIMode={isAIMode}
                  onAiChipDelete={handleChipDelete}
                />
              </Box>
              {renderCandidateJobList()}
            </Box>

            {/* Resizable Divider — desktop only */}
            {!mobile && (
              <Box
                onMouseDown={startCandidateResize}
                sx={{
                  display: { xs: 'none', md: 'block' },
                  flexShrink: 0,
                  width: '4px',
                  cursor: 'col-resize',
                  backgroundColor: 'transparent',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '1px',
                    height: '100%',
                    backgroundColor: theme.palette.divider,
                  },
                  '&:hover, &:active': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              />
            )}

            {/* Job Detail Panel — desktop only */}
            {!mobile && (
              selectedJob ? (
                <JobDetailPanel
                  rankedJob={selectedJob}
                  conversationId={conversationId}
                  onFavoriteClick={onFavoriteClick}
                />
              ) : (
                <Box sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  color: 'text.secondary',
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, color: 'text.secondary' }}>
                    {currentDisplayJobs.length === 0 ? 'No jobs to display' : 'Select a job to view details'}
                  </Typography>
                  <Typography variant="body2" color="text.disabled">
                    {currentDisplayJobs.length === 0
                      ? 'Try adjusting your filters or search criteria'
                      : 'Click on a job card to see the full details here'
                    }
                  </Typography>
                </Box>
              )
            )}

          </Box>
        ) : (
          <>
            <Box sx={{ flexShrink: 0 }}>
              <Filters
                filterType={FilterType.JOB}
                filterValues={filterValues}
                listType='job'
                loading={isLoadingFilters}
                applyFilters={applyJobFilters}
                disabled={isAiTyping || isLoadingJobs || isApplyingFilters}
                resultCount={isAIMode ? displayJobs?.length : totalJobs}
                favoritesOnly={favoritesOnly}
                onToggleFavorites={signedIn && !employerUserId ? toggleFavoritesFilter : undefined}
                isAIMode={isAIMode}
                onAiChipDelete={handleChipDelete}
              />
            </Box>
            {(isLoadingJobs || loading || isApplyingFilters) ? (
              <Loader />
            ) : (
              // Employer/public: traditional list+detail layout
              renderEmployerLayout()
            )}
          </>
        )}
      </Box>

      {/* Floating AI Chat Widget — desktop candidates only */}
      {isCandidate && !mobile && (
        <>
          {/* FAB button */}
          {!chatOpen && (
            <Box
              role="button"
              tabIndex={0}
              aria-label="open Stethi"
              onClick={() => setChatOpen(true)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setChatOpen(true) } }}
              sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                zIndex: 1300,
                display: 'flex',
                alignItems: 'center',
                background: `linear-gradient(135deg, ${theme.palette.ai.dark} 0%, ${theme.palette.ai.main} 100%)`,
                borderRadius: '12px',
                padding: '12px 16px',
                boxShadow: theme.palette.ai.shadow,
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: theme.palette.shadows?.heavy,
                  transform: 'translateY(-2px)',
                },
                '&:active': {
                  transform: 'translateY(0px)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <MyStethiAIIcon sx={{ color: theme.palette.common.white, fontSize: '1.3rem', mr: 1 }} />
              <Typography
                variant="button"
                sx={{
                  color: theme.palette.common.white,
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  textTransform: 'none',
                }}
              >
                Stethi
              </Typography>
            </Box>
          )}

          {/* Floating chat panel — always mounted to preserve conversation state */}
          <Box
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              width: 500,
              height: '75vh',
              maxHeight: 800,
              minHeight: 450,
              zIndex: 1300,
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              border: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden',
              backgroundColor: theme.palette.background.default,
              visibility: chatOpen ? 'visible' : 'hidden',
              opacity: chatOpen ? 1 : 0,
              transform: chatOpen ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.25s ease, transform 0.25s ease, visibility 0.25s ease',
              display: 'flex',
              flexDirection: 'column',
              '& > div': {
                width: '100%',
                borderLeft: 'none !important',
              },
            }}
          >
            <AIChatBot inline onCollapse={() => setChatOpen(false)} />
          </Box>
        </>
      )}

      {/* Chat Bottom Bar — mobile only */}
      {isCandidate && mobile && <ChatBottomBar />}

      {/* Mobile: AIChatBot in drawer mode */}
      {isCandidate && mobile && <AIChatBot />}

      {/* Upload method selection menu */}
      <Menu
        anchorEl={uploadMenuAnchor}
        open={Boolean(uploadMenuAnchor)}
        onClose={handleUploadMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItemComponent onClick={handleManualUpload}>
          <UploadIcon sx={{ mr: 2 }} />
          Manual Upload
        </MenuItemComponent>
      </Menu>

      {/* Manual upload drawer */}
      <JobDrawer
        open={drawerJobOpen}
        onClose={() => setDrawerJobOpen(false)}
        isEdit={false}
        setOpenDrawer={setDrawerJobOpen}
      />

      {/* Smart upload drawer */}
      <SmartUploadDrawer
        open={smartUploadDrawerOpen}
        onClose={() => setSmartUploadDrawerOpen(false)}
      />

      {/* Success notification */}
      <Snackbar
        open={showUploadSuccess}
        autoHideDuration={6000}
        onClose={() => setShowUploadSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowUploadSuccess(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          Successfully uploaded {uploadedJobsCount} job{uploadedJobsCount !== 1 ? 's' : ''}!
          Your new listings are now live.
        </Alert>
      </Snackbar>

      <Modal
        open={showAISearchUpdateModal}
        onClose={handleAISearchUpdateDismiss}
        aria-labelledby="ai-search-update-modal-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          maxWidth: 480,
          mx: 2,
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: theme.palette.ai.shadow,
          overflow: 'hidden',
          outline: 'none',
        }}>
          {/* Gradient header strip with icon */}
          <Box sx={{
            background: `linear-gradient(135deg, ${theme.palette.ai.dark} 0%, ${theme.palette.ai.main} 100%)`,
            px: 4,
            pt: 3.5,
            pb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}>
            <Box sx={{
              width: 52,
              height: 52,
              borderRadius: '14px',
              bgcolor: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <MyStethiAIIcon sx={{ color: theme.palette.common.white, fontSize: '1.75rem' }} />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography
                  id="ai-search-update-modal-title"
                  variant="h6"
                  sx={{ color: theme.palette.common.white, fontWeight: 700, lineHeight: 1.2 }}
                >
                  Introducing Stethi AI
                </Typography>
                <Chip
                  label="Beta"
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: theme.palette.common.white,
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    height: 20,
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.4 }}>
                A smarter layer on top of your job search
              </Typography>
            </Box>
          </Box>

          {/* Body */}
          <Box sx={{ px: 4, pt: 3, pb: 3.5 }}>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2.5, lineHeight: 1.6 }}>
              We've added Stethi AI alongside your existing filters — it works together with them, not instead of them.
            </Typography>

            <Stack spacing={1.5} sx={{ mb: 3 }}>
              {[
                'Ask in plain language — location, schedule, visa support, work-life balance, and more',
                'See your match strength for every job, based on your profile and preferences',
                'Your traditional filters are still here — use both however works for you',
                'It\'s optional and in Beta — your feedback helps us improve it',
              ].map((point) => (
                <Box key={point} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: theme.palette.ai.main,
                    flexShrink: 0,
                    mt: '7px',
                  }} />
                  <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                    {point}
                  </Typography>
                </Box>
              ))}
            </Stack>

            <Button
              fullWidth
              variant="contained"
              onClick={handleAISearchUpdateDismiss}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.ai.dark} 0%, ${theme.palette.ai.main} 100%)`,
                color: theme.palette.common.white,
                fontWeight: 600,
                py: 1.25,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '0.95rem',
                boxShadow: theme.palette.ai.shadow,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.ai.dark} 20%, ${theme.palette.ai.main} 100%)`,
                  boxShadow: theme.palette.shadows?.heavy,
                },
              }}
            >
              Got it!
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  )
}

export default Jobs
